import { makeAutoObservable, observable } from "mobx";
import { ProductionDashboardService } from "../services/productiondashboardservice";

const normalizeKey = (key) => key.toString().replace(/[^a-z0-9]/gi, "").toLowerCase();

const getValue = (source, key) => {
  if (!source) {
    return undefined;
  }

  if (Object.prototype.hasOwnProperty.call(source, key)) {
    return source[key];
  }

  const normalizedKey = normalizeKey(key);
  const sourceKey = Object.keys(source).find(
    (currentKey) => normalizeKey(currentKey) === normalizedKey
  );

  return sourceKey ? source[sourceKey] : undefined;
};

const getPayloadValue = (payload, key) =>
  getValue(payload, key) || getValue(getValue(payload, "data"), key);

const toCollection = (value) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  if (Array.isArray(value.$values)) {
    return value.$values;
  }

  if (typeof value.values === "function") {
    return [...value.values()];
  }

  if (typeof value === "object") {
    return Object.values(value);
  }

  return [];
};

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const normalizeProduction = (production) => ({
  ...production,
  asset: getValue(production, "asset"),
  oilLastUpdate: toFiniteNumber(getValue(production, "oilLastUpdate")),
  gasLastUpdate: toFiniteNumber(getValue(production, "gasLastUpdate")),
  nglLastUpdate: toFiniteNumber(getValue(production, "nglLastUpdate")),
  boeLastUpdate: toFiniteNumber(getValue(production, "boeLastUpdate")),
  oilMTDActual: toFiniteNumber(getValue(production, "oilMTDActual")),
  gasMTDActual: toFiniteNumber(getValue(production, "gasMTDActual")),
  nglMTDActual: toFiniteNumber(getValue(production, "nglMTDActual")),
  boeMTDActual: toFiniteNumber(getValue(production, "boeMTDActual")),
  oilYTDActual: toFiniteNumber(getValue(production, "oilYTDActual")),
  gasYTDActual: toFiniteNumber(getValue(production, "gasYTDActual")),
  nglYTDActual: toFiniteNumber(getValue(production, "nglYTDActual")),
  boeYTDActual: toFiniteNumber(getValue(production, "boeYTDActual")),
});

const normalizeReferDate = (referDate) => ({
  ...referDate,
  asset: getValue(referDate, "asset"),
  productionDate: getValue(referDate, "productionDate"),
});

const toObservableMap = (items, keySelector) => {
  const mappedItems = observable.map({});

  items.forEach((item, index) => {
    mappedItems.set(keySelector(item, index), item);
  });

  return mappedItems;
};

class ProductionDashboardStore {
  constructor() {
    makeAutoObservable(this);
  }

  showProductionDashboard = true;
  isLoading = false;
  loadError = null;
  dashboardDataPromise = null;
  productions = observable.map({});
  referDates = observable.map({});
  async getDashboardData() {
    if (this.dashboardDataPromise) {
      return this.dashboardDataPromise;
    }

    this.dashboardDataPromise = this.loadDashboardData();
    return this.dashboardDataPromise;
  }

  async loadDashboardData() {
    try {
      this.isLoading = true;
      this.loadError = null;
      const retVal =
        await ProductionDashboardService.getproductiondashboardData();
      const productions = toCollection(getPayloadValue(retVal, "productions"))
        .map(normalizeProduction);
      const referDates = toCollection(
        getPayloadValue(retVal, "referDate") || getPayloadValue(retVal, "referDates")
      ).map(normalizeReferDate);

      this.productions = toObservableMap(
        productions,
        (production, index) => production.asset || index
      );
      this.referDates = toObservableMap(
        referDates,
        (referDate, index) => getValue(referDate, "asset") || index
      );

      console.log("prod", [...this.referDates.values()]);

      return retVal;
    } catch (error) {
      this.loadError =
        error?.response?.body?.message ||
        error?.message ||
        "Production dashboard data is currently unavailable.";
      this.productions.clear();
      this.referDates.clear();
      console.error("Production dashboard data could not be loaded:", error);
      return null;
    } finally {
      this.isLoading = false;
      this.dashboardDataPromise = null;
    }
  }
}



const productionDashboardStore = new ProductionDashboardStore();
export default productionDashboardStore;

